import { type ReactNode, useEffect, useCallback } from 'react';

import {
  UserContext,
  type User,
  type UserContextProps,
  type UserMethods,
} from '../contexts/user.context';

import { useLocalStorage } from 'react-use';
import { setUser, clearUser, addBreadcrumb, setContext } from '@/config/sentry.config';

type UserProviderProps = {
  children: ReactNode;
  onLoad?: (user: UserMethods) => void;
};

export function UserProvider({ children, onLoad }: UserProviderProps) {
  const [storedUser, setStoredUser, removeStoredUser] = useLocalStorage<Partial<User> | null>(
    'user',
    null
  );

  const get = useCallback(() => storedUser as User | null, [storedUser]);

  const save = useCallback(
    (user: User) => {
      setStoredUser(user);

      // Configurar usuário no Sentry
      setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });

      // Adicionar contexto do usuário
      setContext('user', {
        id: user.id,
        phone: user.phone,
        name: user.name,
        email: user.email,
        avatar: user.avatar,
      });

      addBreadcrumb('Usuário salvo', 'user', 'info', {
        userId: user.id,
        userName: user.name,
        userPhone: user.phone,
      });
    },
    [setStoredUser]
  );

  const update = useCallback(
    (partialUser: Partial<User>) => {
      setStoredUser(prevUser => {
        if (!prevUser) return { ...partialUser };

        const updatedUser = { ...prevUser, ...partialUser };

        // Atualizar contexto do usuário no Sentry
        if (updatedUser.id) {
          setUser({
            id: updatedUser.id,
            email: updatedUser.email,
            username: updatedUser.name,
          });

          setContext('user', {
            id: updatedUser.id,
            phone: updatedUser.phone,
            name: updatedUser.name,
            email: updatedUser.email,
            avatar: updatedUser.avatar,
          });
        }

        addBreadcrumb('Usuário atualizado', 'user', 'info', {
          updatedFields: Object.keys(partialUser),
        });

        return updatedUser;
      });
    },
    [setStoredUser]
  );

  const clean = useCallback(() => {
    removeStoredUser();

    // Limpar dados do usuário no Sentry
    clearUser();

    addBreadcrumb('Usuário removido', 'user', 'info');
  }, [removeStoredUser]);

  const user: UserContextProps['user'] = {
    get,
    save,
    update,
    clean,
  };

  useEffect(() => {
    onLoad?.(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <UserContext value={{ user }}>{children}</UserContext>;
}
